import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Users, Eye, BookOpenCheck, Star, Clock, Flame, Target, Award,
  FileText, ChevronRight, ArrowUpDown, CheckCircle2, XCircle, Hourglass,
} from "lucide-react";
import OpportunitiesDataView from "./opportunities/OpportunitiesDataView";
import { useAuthContext } from "../hooks/useAuthContext";
import { useLang } from "../hooks/useLang";
import StatTile from "./charts/StatTile";
import ChartCard from "./charts/ChartCard";
import { TimeColumns, TrendArea, RankedBars, Donut, ScoreLine } from "./charts/Charts";
import { formatMinutes, formatCompact } from "./charts/chartTheme";

const RANGES = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "1 year" },
  { value: "all", label: "All time" },
];

const TABS = [
  { id: "audience", label: "Audience", hint: "Who reads and follows you" },
  { id: "learning", label: "Learning", hint: "Your own study progress" },
  { id: "materials", label: "Materials", hint: "How each upload performs" },
  { id: "applications", label: "Applications", hint: "Opportunities you applied to" },
];

// Status is its own colour job — reserved meanings, never reused as a series
// colour, and always paired with an icon and a label so the state survives a
// colourblind reader.
const APPLICATION_STATUS = [
  { key: "accepted", label: "Accepted", icon: CheckCircle2, tone: "text-emerald-600" },
  { key: "pending", label: "Pending", icon: Hourglass, tone: "text-amber-600" },
  { key: "declined", label: "Declined", icon: XCircle, tone: "text-destructive" },
];

const AnalyticsContent = () => {
  const { token } = useAuthContext();
  const { urlLanguage } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const [tab, setTab] = useState(params.get("tab") || "audience");
  const [range, setRange] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("readers");
  const [applications, setApplications] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/analytics/insights?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Could not load your analytics.");
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, range]);

  useEffect(() => { if (token) load(); }, [token, load]);

  // Applications live on the older summary endpoint; fetched once, and only
  // when that tab is actually opened.
  useEffect(() => {
    if (!token || tab !== "applications" || applications.length) return;
    fetch("/api/analytics/summary", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => setApplications(body?.applications || []))
      .catch((err) => console.error("Failed to load applications:", err));
  }, [token, tab, applications.length]);

  const applicationSummary = useMemo(() => {
    const counts = { accepted: 0, pending: 0, declined: 0 };
    applications.forEach((a) => {
      if (counts[a.status] !== undefined) counts[a.status] += 1;
    });
    return counts;
  }, [applications]);

  const applicationsByMonth = useMemo(() => {
    const buckets = {};
    applications.forEach((a) => {
      const when = new Date(a.createdAt || a.appliedAt || Date.now());
      const key = when.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
      buckets[key] = (buckets[key] || 0) + 1;
    });
    return Object.entries(buckets).map(([label, value]) => ({ label, date: label, value }));
  }, [applications]);

  const sortedMaterials = useMemo(() => {
    const rows = [...(data?.materials || [])];
    return rows.sort((a, b) => {
      if (sortBy === "title") return a.title.localeCompare(b.title);
      if (sortBy === "rating") return b.rating - a.rating;
      if (sortBy === "views") return b.views - a.views;
      if (sortBy === "recent") return new Date(b.createdAt) - new Date(a.createdAt);
      return b.readers - a.readers;
    });
  }, [data, sortBy]);

  if (loading && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
        <span className="text-sm text-muted-foreground animate-pulse">Loading analytics...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-[1440px] mx-auto px-6 py-12 text-center">
        <div className="p-8 rounded-2xl bg-card border border-border shadow-xl max-w-md mx-auto">
          <h3 className="text-lg font-bold text-foreground mb-2">Analytics unavailable</h3>
          <p className="text-sm text-muted-foreground mb-6">{error || "Something went wrong."}</p>
          <button
            onClick={load}
            className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors border-none cursor-pointer"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { headline, audience, learning } = data;
  const noAudience = !audience.readers.some((d) => d.value) && !audience.profileViews.some((d) => d.value);

  return (
    // Held at reduced opacity while refetching rather than flashing a
    // skeleton, so switching range never jumps the layout.
    <div className={`max-w-[1440px] mx-auto px-6 py-6 transition-opacity ${loading ? "opacity-60" : "opacity-100"}`}>

      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {TABS.find((t) => t.id === tab)?.hint}
          </p>
        </div>

        {/* One filter row, above everything it scopes — every chart on the
            page re-renders against the same slice. */}
        <div className="flex items-center gap-1 bg-card border border-border/60 rounded-xl p-1" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              aria-pressed={range === r.value}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border-none cursor-pointer ${
                range === r.value ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Headline numbers stay visible across tabs — they are the summary the
          rest of the page explains. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatTile icon={BookOpenCheck} label="Readers" value={formatCompact(headline.readers.value)} change={headline.readers.change} />
        <StatTile icon={Users} label="Followers" value={formatCompact(headline.followers.value)} change={headline.followers.change} hint={`+${headline.followers.gained} this period`} />
        <StatTile icon={Eye} label="Profile views" value={formatCompact(headline.profileViews.value)} change={headline.profileViews.change} />
        <StatTile
          icon={Star}
          label="Avg rating"
          value={headline.avgRating.count ? headline.avgRating.value : "—"}
          hint={headline.avgRating.count ? `from ${headline.avgRating.count} rating${headline.avgRating.count === 1 ? "" : "s"}` : "no ratings yet"}
        />
      </div>

      <nav className="flex items-center gap-1 border-b border-border mb-6" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => { setTab(t.id); navigate(urlLanguage(`/analytics?tab=${t.id}`), { replace: true }); }}
            className={`relative px-4 py-3 text-sm bg-transparent border-none cursor-pointer transition-colors ${
              tab === t.id ? "text-foreground font-bold" : "text-muted-foreground hover:text-foreground font-medium"
            }`}
          >
            {t.label}
            <span
              aria-hidden="true"
              className={`absolute bottom-0 left-3 right-3 h-[2px] rounded-full bg-primary origin-center transition-transform duration-300 ${
                tab === t.id ? "scale-x-100" : "scale-x-0"
              }`}
            />
          </button>
        ))}
      </nav>

      {/* ── Audience ─────────────────────────────────────────────────────── */}
      {tab === "audience" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard
            title="Follower growth"
            subtitle={`${headline.followers.value} total · ${headline.followers.gained} gained this period`}
            empty={!audience.followerGrowth.length ? { title: "No followers yet", hint: "Publish materials publicly and people can follow your work." } : null}
            table={{ head: ["Date", "Followers"], rows: audience.followerGrowth.map((d) => [d.label, d.value]) }}
          >
            <TrendArea data={audience.followerGrowth} valueLabel="Followers" />
          </ChartCard>

          <ChartCard
            title="Profile views"
            subtitle={`${headline.profileViews.value} visit${headline.profileViews.value === 1 ? "" : "s"} · counted once per person per day`}
            empty={!audience.profileViews.some((d) => d.value) ? { title: "No profile visits yet", hint: "Share your profile link — visits show up here the same day." } : null}
            table={{ head: ["Date", "Views"], rows: audience.profileViews.map((d) => [d.label, d.value]) }}
          >
            <TimeColumns data={audience.profileViews} valueLabel="Views" />
          </ChartCard>

          <ChartCard
            title="People reading your materials"
            subtitle={`${headline.readers.value} reader${headline.readers.value === 1 ? "" : "s"} in this period`}
            empty={!audience.readers.some((d) => d.value) ? { title: "No reads yet", hint: "Once someone opens one of your materials it is counted here." } : null}
            table={{ head: ["Date", "Reads"], rows: audience.readers.map((d) => [d.label, d.value]) }}
          >
            <TimeColumns data={audience.readers} valueLabel="Reads" />
          </ChartCard>

          <ChartCard
            title="What your audience reads"
            subtitle="Reads by material type"
            empty={!audience.audienceSplit.length ? { title: "Nothing read yet", hint: "This splits your reads across the kinds of material you publish." } : null}
            table={{ head: ["Type", "Reads"], rows: audience.audienceSplit.map((d) => [d.name, d.value]) }}
          >
            <Donut data={audience.audienceSplit} totalLabel="reads" />
          </ChartCard>

          <ChartCard
            title="How people score your work"
            subtitle={headline.avgRating.count ? `${headline.avgRating.value} average across ${headline.avgRating.count} rating${headline.avgRating.count === 1 ? "" : "s"}` : "No ratings yet"}
            empty={!headline.avgRating.count ? { title: "No ratings yet", hint: "Readers can score a material out of 10 after opening it." } : null}
            table={{ head: ["Score", "Ratings"], rows: audience.ratingDistribution.filter((d) => d.count).map((d) => [`${d.score}/10`, d.count]) }}
          >
            <RankedBars
              data={audience.ratingDistribution.filter((d) => d.count).map((d) => ({ name: `${d.score}/10`, value: d.count }))}
              valueLabel="Ratings"
            />
          </ChartCard>

          {noAudience && (
            <div className="lg:col-span-2 p-5 rounded-2xl bg-muted/40 border border-border/40">
              <p className="text-sm font-bold text-foreground">Nothing to measure yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Audience numbers start moving once your materials are public and your profile link is out there.
              </p>
              <button
                onClick={() => navigate(urlLanguage("/library"))}
                className="mt-3 flex items-center gap-1.5 text-[11px] font-bold text-primary bg-transparent border-none cursor-pointer hover:underline p-0"
              >
                Go to your library <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Learning ─────────────────────────────────────────────────────── */}
      {tab === "learning" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <StatTile icon={Clock} label="Time studied" value={formatMinutes(learning.totalMinutes)} hint="in this period" />
            <StatTile icon={Flame} label="Current streak" value={`${learning.streak}`} hint={learning.streak === 1 ? "day" : "days"} />
            <StatTile icon={Target} label="Active days" value={`${learning.activeDays}`} hint="5+ minutes studied" />
            <StatTile icon={Award} label="Avg quiz score" value={learning.quizCount ? `${learning.avgScore}%` : "—"} hint={learning.quizCount ? `${learning.quizCount} quiz${learning.quizCount === 1 ? "" : "zes"}` : "no quizzes yet"} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              title="Study time"
              subtitle={`${formatMinutes(learning.totalMinutes)} over ${learning.activeDays} active day${learning.activeDays === 1 ? "" : "s"}`}
              empty={!learning.studyMinutes.some((d) => d.value) ? { title: "No study time yet", hint: "Time is counted while a material is open in the reader." } : null}
              table={{ head: ["Date", "Studied"], rows: learning.studyMinutes.map((d) => [d.label, formatMinutes(d.value)]) }}
            >
              <TimeColumns data={learning.studyMinutes} valueLabel="Studied" format={formatMinutes} />
            </ChartCard>

            <ChartCard
              title="Points earned"
              subtitle="Running total, rebuilt from uploads, quizzes and study time"
              empty={!learning.pointsTimeline.length ? { title: "No points yet", hint: "Uploading, studying and finishing quizzes all add points." } : null}
              table={{ head: ["Date", "Points"], rows: learning.pointsTimeline.map((d) => [d.label, d.value]) }}
            >
              <TrendArea data={learning.pointsTimeline} valueLabel="Points" />
            </ChartCard>

            <ChartCard
              title="Quiz scores"
              subtitle={learning.quizCount ? `${learning.quizCount} quiz${learning.quizCount === 1 ? "" : "zes"} · ${learning.avgScore}% average` : "No quizzes in this period"}
              empty={!learning.quizScores.length ? { title: "No quiz results yet", hint: "Generate a quiz from any material and your score lands here." } : null}
              table={{ head: ["Date", "Score"], rows: learning.quizScores.map((d) => [d.label, `${d.value}%`]) }}
            >
              <ScoreLine data={learning.quizScores} />
            </ChartCard>

            <ChartCard
              title="What you spend time on"
              subtitle="Your library by material type"
              empty={!audience.audienceSplit.length ? { title: "Nothing to split yet", hint: "Add materials and this breaks them down by type." } : null}
              table={{ head: ["Type", "Materials"], rows: audience.audienceSplit.map((d) => [d.name, d.value]) }}
            >
              <Donut data={audience.audienceSplit} totalLabel="reads" />
            </ChartCard>
          </div>
        </>
      )}

      {/* ── Materials ────────────────────────────────────────────────────── */}
      {tab === "materials" && (
        <div className="space-y-4">
          {sortedMaterials.length > 0 && (
            <ChartCard
              title="Most read materials"
              subtitle="Distinct people, not repeat opens"
              table={{ head: ["Material", "Readers"], rows: sortedMaterials.slice(0, 10).map((m) => [m.title, m.readers]) }}
            >
              <RankedBars
                data={sortedMaterials.slice(0, 8).map((m) => ({
                  name: m.title.length > 18 ? `${m.title.slice(0, 17)}…` : m.title,
                  value: m.readers,
                }))}
                valueLabel="Readers"
              />
            </ChartCard>
          )}

          <section className="bg-card rounded-2xl border border-border/40 shadow-sm overflow-hidden">
            <header className="flex items-center justify-between gap-3 p-5 pb-4">
              <div>
                <h3 className="text-sm font-bold text-foreground">Every material</h3>
                <p className="text-xs text-muted-foreground mt-0.5 font-medium">
                  {sortedMaterials.length} upload{sortedMaterials.length === 1 ? "" : "s"}
                </p>
              </div>
              <label className="flex items-center gap-2 text-[11px] font-bold text-muted-foreground">
                <ArrowUpDown className="w-3.5 h-3.5" aria-hidden="true" />
                <span className="sr-only">Sort materials by</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-muted border-0 rounded-lg px-2 py-1.5 text-[11px] font-bold text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="readers">Readers</option>
                  <option value="views">Views</option>
                  <option value="rating">Rating</option>
                  <option value="recent">Newest</option>
                  <option value="title">Title</option>
                </select>
              </label>
            </header>

            {sortedMaterials.length === 0 ? (
              <div className="px-5 pb-8 pt-2 text-center">
                <p className="text-sm font-semibold text-foreground">You have not uploaded anything yet</p>
                <p className="text-xs text-muted-foreground mt-1">Every upload gets its own row here, with readers and ratings.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-y border-border/40 bg-muted/30">
                      <th scope="col" className="text-left font-bold text-muted-foreground px-5 py-2.5">Material</th>
                      <th scope="col" className="text-right font-bold text-muted-foreground px-3 py-2.5">Readers</th>
                      <th scope="col" className="text-right font-bold text-muted-foreground px-3 py-2.5">Views</th>
                      <th scope="col" className="text-right font-bold text-muted-foreground px-5 py-2.5">Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMaterials.map((m) => (
                      <tr
                        key={m.id}
                        onClick={() => navigate(urlLanguage(`/material/${m.id}`))}
                        className="border-b border-border/30 last:border-0 hover:bg-muted/40 cursor-pointer transition-colors"
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="relative w-8 h-8 rounded-lg overflow-hidden bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                              {m.cover_url
                                ? <img src={m.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                : <FileText className="w-3.5 h-3.5" />}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-foreground truncate max-w-[240px]">{m.title}</p>
                              <p className="text-[10px] text-muted-foreground mt-0.5">
                                {m.category}
                                {m.visibility !== "Public" && ` · ${m.visibility}`}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="text-right font-bold text-foreground tabular-nums px-3 py-3">{formatCompact(m.readers)}</td>
                        <td className="text-right text-muted-foreground tabular-nums px-3 py-3">{formatCompact(m.views)}</td>
                        <td className="text-right px-5 py-3 tabular-nums">
                          {m.ratingCount
                            ? <span className="font-bold text-foreground">{m.rating} <span className="text-muted-foreground font-medium">({m.ratingCount})</span></span>
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
      {/* ── Applications ─────────────────────────────────────────────────── */}
      {tab === "applications" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile icon={Target} label="Applied" value={applications.length} hint="all time" />
            {APPLICATION_STATUS.map((st) => (
              <StatTile key={st.key} icon={st.icon} label={st.label} value={applicationSummary[st.key]} hint={`of ${applications.length || 0}`} />
            ))}
          </div>

          {applications.length > 0 && (
            <div className="grid gap-4 lg:grid-cols-2">
              <ChartCard
                title="Applications over time"
                subtitle="When you applied"
                table={{ head: ["Month", "Applications"], rows: applicationsByMonth.map((d) => [d.label, d.value]) }}
              >
                <TimeColumns data={applicationsByMonth} valueLabel="Applications" />
              </ChartCard>

              <ChartCard
                title="Outcomes"
                subtitle="Where your applications landed"
                table={{ head: ["Outcome", "Count"], rows: APPLICATION_STATUS.map((st) => [st.label, applicationSummary[st.key]]) }}
              >
                <Donut
                  data={APPLICATION_STATUS.map((st) => ({ name: st.label, value: applicationSummary[st.key] })).filter((d) => d.value)}
                  totalLabel="applied"
                />
              </ChartCard>
            </div>
          )}

          <OpportunitiesDataView applications={applications} loading={false} />
        </div>
      )}
    </div>
  );
};

export default AnalyticsContent;
